package org.teddapp.project.ui.notes

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.input.TextFieldState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import androidx.compose.foundation.Image
import androidx.compose.ui.graphics.ImageBitmap
import org.jetbrains.compose.resources.ExperimentalResourceApi
import org.jetbrains.compose.resources.decodeToImageBitmap
import com.tedd.db.Note
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import org.teddapp.project.db.nowMillis
import org.teddapp.project.sync.TeddFiles
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.admin.PickedImage
import org.teddapp.project.ui.admin.TeddImagePicker
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * Les images d'une note. Une image est un fichier joint : une ligne `attachment`
 * synchronisée, ses octets gardés sur l'appareil jusqu'à leur envoi, et une ligne
 * `![](file:<id>)` qui la place dans le texte.
 */
internal object NoteImages {

    /** Un nom de fichier lisible d'après le type : le serveur en veut un. */
    private fun fileName(type: String) = "image." + when (type) {
        "image/png" -> "png"
        "image/webp" -> "webp"
        else -> "jpg"
    }

    /**
     * Range l'image choisie : ses octets sur l'appareil, sa ligne `attachment` en attente
     * d'envoi. L'identifiant est un UUID : le serveur l'emploie tel quel, sans table de
     * correspondance.
     */
    @OptIn(ExperimentalUuidApi::class)
    suspend fun store(db: TeddDatabase, note: Note, image: PickedImage): String = withContext(Dispatchers.Default) {
        val id = Uuid.random().toString()
        val now = nowMillis()
        db.transaction {
            db.fileBlobQueries.put(id, image.bytes, now)
            db.attachmentQueries.insert(
                id, "note", note.id, fileName(image.contentType), image.contentType,
                image.bytes.size.toLong(), null, null, null, null, "pending", 0L, now, now,
            )
        }
        id
    }

    /**
     * Retire l'image de la note. Le fichier n'est supprimé que si cette note en est la
     * propriétaire : une copie de note cite les images de l'original sans les posséder.
     */
    suspend fun remove(db: TeddDatabase, noteId: String, id: String) = withContext(Dispatchers.Default) {
        val a = db.attachmentQueries.selectById(id).executeAsOneOrNull() ?: return@withContext
        if (a.owner_id != noteId) return@withContext
        val now = nowMillis()
        db.transaction {
            db.attachmentQueries.softDelete(now, now, id)
            db.fileBlobQueries.remove(id)
        }
    }

    /** Les images que la note possède et que son texte ne cite plus. */
    suspend fun pruneOrphans(db: TeddDatabase, note: Note) = withContext(Dispatchers.Default) {
        val cited = note.body.split('\n').mapNotNullTo(mutableSetOf()) { MarkdownEdits.fileIdOf(it) }
        db.attachmentQueries.selectByOwner("note", note.id).executeAsList()
            .filter { it.id !in cited }
            .forEach { remove(db, note.id, it.id) }
    }
}

/**
 * Choisit une image sur l'appareil et la pose au curseur de [body]. Rend un message à
 * montrer en cas d'échec, ou null.
 */
internal suspend fun pickImageInto(db: TeddDatabase, note: Note, body: TextFieldState): String? {
    val pick = TeddImagePicker.pick ?: return "Pas de sélecteur d'images sur cet appareil."
    val image = runCatching { pick() }.getOrNull() ?: return null
    if (image.bytes.size > TeddImagePicker.MAX_BYTES) return "Cette image est trop lourde (6 Mo au plus)."
    val id = NoteImages.store(db, note, image)
    body.format { t, s, _ -> MarkdownEdits.block(t, s, MarkdownEdits.fileRef(id)) }
    return null
}

/**
 * L'image, chargée et décodée hors du fil de l'interface. Décodée par Compose et non
 * par Coil : on a déjà les octets, et Coil ne démarre pas dans le navigateur.
 */
@OptIn(ExperimentalResourceApi::class)
@Composable
private fun rememberImage(db: TeddDatabase, id: String): Pair<Boolean, ImageBitmap?> {
    var loading by remember(id) { mutableStateOf(true) }
    var image by remember(id) { mutableStateOf<ImageBitmap?>(null) }
    LaunchedEffect(id) {
        image = withContext(Dispatchers.Default) {
            TeddFiles.bytes(db, id)?.let { runCatching { it.decodeToImageBitmap() }.getOrNull() }
        }
        loading = false
    }
    return loading to image
}

/** Une image dans la note, à la largeur de la page. Un tap l'ouvre en grand. */
@Composable
internal fun NoteImage(db: TeddDatabase, id: String, onOpen: (() -> Unit)?) {
    val (loading, image) = rememberImage(db, id)
    val shape = RoundedCornerShape(TeddTokens.RMd)
    if (image != null) {
        Image(
            bitmap = image,
            contentDescription = null,
            contentScale = ContentScale.FillWidth,
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(max = 520.dp)
                .clip(shape)
                .let { if (onOpen != null) it.clickable(onClick = onOpen) else it },
        )
    } else {
        Box(
            Modifier.fillMaxWidth().height(120.dp).background(TeddTokens.SurfaceSunken, shape).border(1.dp, TeddTokens.Line, shape),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                if (loading) "Chargement de l'image…" else "Image indisponible ici pour l'instant",
                style = TeddType.Caption,
                color = TeddTokens.InkMuted,
            )
        }
    }
}

/** L'image en grand, sur fond sombre, avec de quoi la retirer de la note. */
@Composable
internal fun NoteImageViewer(db: TeddDatabase, id: String, onRemove: () -> Unit, onClose: () -> Unit) {
    val (_, image) = rememberImage(db, id)
    Dialog(onDismissRequest = onClose, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(Modifier.fillMaxSize().background(Color.Black).safeDrawingPadding()) {
            Row(
                Modifier.fillMaxWidth().padding(TeddSpace.S3),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
            ) {
                TeddButton(text = "Fermer", onClick = onClose, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm, icon = "x")
                Spacer(Modifier.weight(1f))
                TeddButton(text = "Retirer de la note", onClick = onRemove, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm, icon = "trash")
            }
            Box(Modifier.fillMaxWidth().weight(1f).clickable(onClick = onClose), contentAlignment = Alignment.Center) {
                if (image != null) {
                    Image(bitmap = image, contentDescription = null, contentScale = ContentScale.Fit, modifier = Modifier.fillMaxSize())
                }
            }
        }
    }
}
